import { AccountDetailsAccordion } from '@client/components/ltc-financial-planning/AccountDetailsAccordion';
import { AddAssetSection } from '@client/components/ltc-financial-planning/AddAssetSection';
import {
  selectClient,
  selectLtcFinancialPlanningSelectedAccountTypes,
} from '@client/model/selectors';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { StepContentProps } from '../types';

export function AssetDetailsStep({ navigation }: StepContentProps) {
  const client = useSelector(selectClient);
  const clientFirstName = client.clientFirstName;
  const selectedAccountTypes = useSelector(selectLtcFinancialPlanningSelectedAccountTypes);

  // Check if there are any existing self-funding sources
  const selfFunding = client.multipleFundingSources?.selfFunding || {};

  // Helper function to check if all self-funding sources are complete
  const allAccountsComplete = useMemo(() => {
    return selectedAccountTypes.every(accountType => {
      const matchingSource = Object.values(selfFunding).find(
        (source: SelfFundingSource) =>
          source.accountType === accountType || source.selfFundingName === accountType,
      );

      if (!matchingSource) return false;

      return (
        matchingSource.existingAssetsValue !== null &&
        matchingSource.annualRateOfReturn !== null &&
        matchingSource.gainsTaxRate !== null
      );
    });
  }, [selectedAccountTypes, selfFunding]);

  return (
    <div className="py-8">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-gray-900 md:text-3xl">
          {allAccountsComplete
            ? `${clientFirstName}'s account details`
            : "Let's get some details on your accounts"}
        </h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-gray-700">
          Click on each account to add the financial details. This helps us accurately project your
          long-term care coverage capacity.
        </p>
      </div>

      <AccountDetailsAccordion
        className="mx-auto mt-8 max-w-3xl"
        selectedAccountTypes={selectedAccountTypes}
      />

      <AddAssetSection className="mx-auto mt-4 max-w-3xl" />

      {!allAccountsComplete && (
        <p className="mt-4 text-center text-sm text-gray-700">
          Please complete all account details to continue
        </p>
      )}
    </div>
  );
}
